import { WATERMARK_LOOP_SECONDS } from './resolveWatermarkStart';

/**
 * Frame state of the animated watermark at `t` seconds into its 8s loop. A
 * pure port of the approved HyperFrames composition
 * (~/devel/autoclipper-os/videos/autoclipper-watermark/index.html): same swap
 * points, staggers and GSAP eases, so a render at any frame is deterministic
 * and the loop is seamless (t → 8 equals t = 0).
 */

const T = WATERMARK_LOOP_SECONDS;
/** Pill widths (design px) for lines A, B, C — measured from the approved build. */
export const PILL_WIDTHS = [561, 684, 556] as const;
/** Swap start times: A→B, B→C, C→A. */
export const SWAP_AT = [T / 3 - 0.68, (2 * T) / 3 - 0.68, T - 0.68] as const;
const OUT = { dur: 0.22, stagger: 0.02 };
const IN = { delay: 0.22, dur: 0.32, stagger: 0.03 };
const PILL_MORPH = 0.5;
const PARKED = 110;
export const ITEMS_PER_LINE = 5;

/** The "clipper" box outline the trace runs around (design px). */
export const TRACE_RECT = { x: 362.4, y: 117.4, width: 453.4, height: 158, rx: 21 } as const;
export const TRACE_PERIMETER =
  2 * (TRACE_RECT.width + TRACE_RECT.height) - 8 * TRACE_RECT.rx + 2 * Math.PI * TRACE_RECT.rx;

export interface WatermarkFrameState {
  pillWidth: number;
  haloOpacity: number;
  /** yPercent per item for lines A, B, C (0 = in place; ±110 = hidden by the mask). */
  lineItemY: [number[], number[], number[]];
  sparkle: { mainScale: number; mainRotation: number; smallScaleA: number; smallScaleB: number };
  /** 0 = 16:9 rectangle, 1 = 9:16 (may overshoot: back.out). */
  reframeProgress: number;
  /** dashOffset as a fraction of the perimeter: 1 = nothing drawn, 0 = full outline. */
  trace: { opacity: number; dashOffset: number };
  scissorsAngle: number;
  chipScale: number;
  /** Shine band translateX, % of the band's own width. */
  shineXPercent: number;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const progress = (t: number, start: number, dur: number) => clamp01((t - start) / dur);
const lerp = (a: number, b: number, p: number) => a + (b - a) * p;

// GSAP eases: power1 = quad, power2 = cubic, power3 = quart.
const ease = {
  power1Out: (p: number) => 1 - (1 - p) ** 2,
  power1InOut: (p: number) => (p < 0.5 ? 2 * p * p : 1 - (-2 * p + 2) ** 2 / 2),
  power2In: (p: number) => p ** 3,
  power2Out: (p: number) => 1 - (1 - p) ** 3,
  power2InOut: (p: number) => (p < 0.5 ? 4 * p ** 3 : 1 - (-2 * p + 2) ** 3 / 2),
  power3Out: (p: number) => 1 - (1 - p) ** 4,
  power3InOut: (p: number) => (p < 0.5 ? 8 * p ** 4 : 1 - (-2 * p + 2) ** 4 / 2),
  sineInOut: (p: number) => -(Math.cos(Math.PI * p) - 1) / 2,
  backOut: (p: number, s = 1.6) => 1 + (s + 1) * (p - 1) ** 3 + s * (p - 1) ** 2,
};

/** GSAP yoyo: forward, backward, … `repeat + 1` plays; returns the eased amount. */
function yoyo(t: number, start: number, dur: number, repeat: number, easeFn: (p: number) => number): number {
  if (t < start) return 0;
  const plays = repeat + 1;
  const elapsed = t - start;
  if (elapsed >= dur * plays) return plays % 2 === 0 ? 0 : 1;
  const cycle = Math.floor(elapsed / dur);
  const local = (elapsed - cycle * dur) / dur;
  return cycle % 2 === 0 ? easeFn(local) : easeFn(1 - local);
}

function lineItemY(line: 0 | 1 | 2, item: number, t: number): number {
  const outAt = SWAP_AT[line] + item * OUT.stagger;
  const inAt = SWAP_AT[(line + 2) % 3] + IN.delay + item * IN.stagger;
  const outY = -PARKED * ease.power2In(progress(t, outAt, OUT.dur));
  const inY = PARKED * (1 - ease.power3Out(progress(t, inAt, IN.dur)));
  if (inAt < outAt) {
    // Lines B and C: parked below → slide in → slide out.
    if (t < inAt) return PARKED;
    if (t < outAt) return inY;
    return outY;
  }
  // Line A: in place at t = 0, leaves at SWAP_AT[0], comes back at the end.
  if (t < outAt) return 0;
  if (t < inAt) return outY;
  return inY;
}

function pillWidth(t: number): number {
  for (let i = 0; i < 3; i += 1) {
    const s = SWAP_AT[i];
    if (t >= s && t < s + PILL_MORPH) {
      return lerp(PILL_WIDTHS[i], PILL_WIDTHS[(i + 1) % 3], ease.power3InOut((t - s) / PILL_MORPH));
    }
  }
  if (t < SWAP_AT[0]) return PILL_WIDTHS[0];
  if (t < SWAP_AT[1]) return PILL_WIDTHS[1];
  if (t < SWAP_AT[2]) return PILL_WIDTHS[2];
  return PILL_WIDTHS[0];
}

function haloOpacity(t: number): number {
  const half = T / 6;
  const k = Math.floor(t / half);
  const e = ease.sineInOut((t - k * half) / half);
  return k % 2 === 0 ? lerp(0.35, 0.8, e) : lerp(0.8, 0.35, e);
}

const SHINE_FROM = -130;
const SHINE_TO = 420;
const SHINE_DUR = 1;
const SHINE_AT = [0.45, SWAP_AT[1] + 1.25];

function shineXPercent(t: number): number {
  for (const s of SHINE_AT) {
    if (t >= s && t < s + SHINE_DUR) return lerp(SHINE_FROM, SHINE_TO, ease.power2InOut((t - s) / SHINE_DUR));
  }
  return SHINE_FROM;
}

function trace(t: number): { opacity: number; dashOffset: number } {
  const start = SWAP_AT[0] + 0.95;
  const fadeAt = SWAP_AT[0] + 1.8;
  if (t < start) return { opacity: 0, dashOffset: 1 };
  const dashOffset = 1 - ease.power2InOut(progress(t, start, 0.8));
  const opacity = t < fadeAt
    ? ease.power1Out(progress(t, start, 0.12))
    : 1 - ease.power1Out(progress(t, fadeAt, 0.45));
  return { opacity, dashOffset };
}

export function watermarkTimeline(rawT: number): WatermarkFrameState {
  const t = ((rawT % T) + T) % T;
  const items = (line: 0 | 1 | 2) => Array.from({ length: ITEMS_PER_LINE }, (_, i) => lineItemY(line, i, t));
  const sparkMain = yoyo(t, 0.3, 0.28, 1, ease.power2Out);
  const reframeStart = SWAP_AT[0] + 0.75;
  return {
    pillWidth: pillWidth(t),
    haloOpacity: haloOpacity(t),
    lineItemY: [items(0), items(1), items(2)],
    sparkle: {
      mainScale: 1 + 0.22 * sparkMain,
      mainRotation: 45 * sparkMain,
      smallScaleA: 1 - 0.65 * yoyo(t, 0.35, 0.18, 3, ease.sineInOut),
      smallScaleB: 1 - 0.65 * yoyo(t, 0.47, 0.18, 3, ease.sineInOut),
    },
    reframeProgress: t < reframeStart ? 0 : ease.backOut(progress(t, reframeStart, 0.5)),
    trace: trace(t),
    scissorsAngle: 16 * yoyo(t, SWAP_AT[1] + 0.7, 0.14, 3, ease.power1InOut),
    chipScale: 1 + 0.1 * yoyo(t, SWAP_AT[1] + 1.35, 0.2, 1, ease.power2Out),
    shineXPercent: shineXPercent(t),
  };
}
